import * as React from "react";
import {
  useNavigate,
} from "react-router-dom";
import { reset as amplitudeReset, setOptOut as amplitudeSetOptOut } from '@amplitude/analytics-browser'
import AuthService from '../services/AuthService'
import UserModel from '../models/User'
import { syncAmplitudeIdentity, clearAmplitudeImpersonation } from '../services/amplitudeIdentity'
import { stopGuidesAndSurveys } from '../services/amplitudeBootstrap'
import _ from 'underscore';

let AuthContext = React.createContext(null);

function Provider(props) {
  let [user, setUser] = React.useState(() => AuthService.getAuthenticatedLocalUser());
  let navigate = useNavigate()
  let [spoofing, setSpoofing] = React.useState(!!localStorage.getItem('spoof_origin_user'))
  let login = React.useCallback(async (_user) => {
    setUser(_user)
    AuthService.setCurrentUser(_user)
    return _user
  }, [])
  let logout = React.useCallback(async () => {
    const reloadRequired = stopGuidesAndSurveys()
    setUser(null);
    amplitudeReset()
    amplitudeSetOptOut(true)
    try {
      return await AuthService.logout()
    } finally {
      localStorage.removeItem('spoof_origin_user')
      clearAmplitudeImpersonation()
      if (reloadRequired) {
        window.location.replace('/login')
      } else {
        navigate('/login', { replace: true })
      }
    }
  }, [navigate])
  let checkPermission = React.useCallback((permission_slug) => {
    if (!user) {
      return false
    }

    let result = false
    const userModel = new UserModel(user)
    if( _.isArray(permission_slug) ) {
      permission_slug.forEach(_slug => {
        if( !result && userModel.hasPermission(_slug) ) {
          result = true
        }
      })
    } else {
      result = userModel.hasPermission(permission_slug)
    }
    return result
  }, [user])
  let spoofUser = React.useCallback(async (user_id) => {
    const spoof_user = await AuthService.spoofUser(user_id)
    const reloadRequired = stopGuidesAndSurveys()
    if( !localStorage.getItem('spoof_origin_user') ) {
      const current_user = AuthService.getLocalUser()
      localStorage.setItem('spoof_origin_user', JSON.stringify(current_user))
    }
    AuthService.setCurrentUser(spoof_user, true)
    setUser(spoof_user)
    setSpoofing(true)
    if (reloadRequired) {
      window.location.reload()
      return true
    }
    // Keep the Amplitude identity on the staff member, not the retailer being spoofed.
    syncAmplitudeIdentity({ user: spoof_user, client: spoof_user?.client })
    return true
  }, [])
  let spoofBack = React.useCallback(() => {
    const reloadRequired = stopGuidesAndSurveys()
    const origin_user = JSON.parse(localStorage.getItem('spoof_origin_user'))
    AuthService.setCurrentUser(origin_user)
    localStorage.removeItem('spoof_origin_user')
    setUser(origin_user)
    setSpoofing(false)
    clearAmplitudeImpersonation()
    if (reloadRequired) {
      window.location.reload()
      return
    }
    syncAmplitudeIdentity({ user: origin_user, client: origin_user?.client })
  }, [])
  let inject = React.useMemo(() => ({
    user: user ? new UserModel(user) : null,
    spoofing,
    login, 
    logout, 
    setUser,
    checkPermission,
    spoofUser,
    spoofBack,
  }), [user, spoofing, login, logout, setUser, checkPermission, spoofUser, spoofBack])
  return <AuthContext.Provider value={inject}>{props.children}</AuthContext.Provider>;
}

function useAuth() {
  const auth_context = React.useContext(AuthContext);

  if (!auth_context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return auth_context
}

export default {
  Provider,
  useAuth,
}